import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  useSetHighRiskUserOverrideReason,
  type MachineryDirectiveCompliance,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'
import {
  HighRiskOverrideDialog,
  OverrideReasonSchema,
  type HighRiskOverrideDialogVariant,
} from './HighRiskOverrideDialog'
import { deriveHighRiskDialogAnswer } from '../steps/settings/ProductMetadataHighRiskReasonDialog'
import type {
  HighRiskCheckDisplay,
  OnboardingStep,
} from './machineryDirectiveOnboardingTypes'

type HighRiskView =
  | 'checking'
  | 'intro'
  | 'user_override_locked'
  | 'agent_locked'
  | 'continue'

type OverrideDialogVariant = HighRiskOverrideDialogVariant

type OnboardingHighRiskStepProps = {
  complianceId: string
  onboardingStep: OnboardingStep
  highRiskCheck: HighRiskCheckDisplay | null
  highRiskUserOverrideReason: string | null
  isUserElevatedHighRiskLocked: boolean
  workflowResultIsHighRisk: boolean | null
  isHighRiskWorkflowRunning: boolean
  highRiskWorkflowError: string | null
  onBeginHighRisk: () => void
  onCloseHighRisk: () => void
  onContinue: () => void
  onHighRiskClassificationUpdated?: (
    updatedCompliance?: MachineryDirectiveCompliance
  ) => void
}

function resolveHighRiskView(params: {
  onboardingStep: OnboardingStep
  isHighRiskWorkflowRunning: boolean
  isAgentHighRisk: boolean
  isUserElevatedHighRiskLocked: boolean
  hasHighRiskUserOverrideReason: boolean
}): HighRiskView | null {
  if (params.onboardingStep === 'high_risk') {
    if (params.isHighRiskWorkflowRunning) {
      return 'checking'
    }
    return 'intro'
  }
  if (
    params.onboardingStep === 'user_high_risk_override_locked' &&
    params.isUserElevatedHighRiskLocked
  ) {
    return 'user_override_locked'
  }
  if (
    params.onboardingStep === 'high_risk_result' ||
    params.onboardingStep === 'user_high_risk_override_locked'
  ) {
    if (params.isAgentHighRisk && !params.hasHighRiskUserOverrideReason) {
      return 'agent_locked'
    }
    return 'continue'
  }
  return null
}

function HighRiskResultListPanel({
  isHighRisk,
  children,
}: {
  isHighRisk: boolean
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        isHighRisk
          ? 'border-amber-200 bg-amber-50/80 dark:border-amber-800 dark:bg-amber-950/30'
          : 'border-green-200 bg-green-50/80 dark:border-green-800 dark:bg-green-950/30'
      )}
    >
      {children}
    </div>
  )
}

function highRiskResultListClassName(isHighRisk: boolean): string {
  return cn(
    'list-outside list-disc space-y-4 pl-6 text-base leading-relaxed text-foreground',
    isHighRisk
      ? 'marker:text-amber-800 dark:marker:text-amber-200'
      : 'marker:text-green-800 dark:marker:text-green-200'
  )
}

export function OnboardingHighRiskStep({
  complianceId,
  onboardingStep,
  highRiskCheck,
  highRiskUserOverrideReason,
  isUserElevatedHighRiskLocked,
  workflowResultIsHighRisk,
  isHighRiskWorkflowRunning,
  highRiskWorkflowError,
  onBeginHighRisk,
  onCloseHighRisk,
  onContinue,
  onHighRiskClassificationUpdated,
}: OnboardingHighRiskStepProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const setHighRiskUserOverrideReasonMutation =
    useSetHighRiskUserOverrideReason(complianceId)
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [overrideDialogVariant, setOverrideDialogVariant] =
    useState<OverrideDialogVariant>('disagreeAutomatedHighRisk')
  const [draftReason, setDraftReason] = useState('')
  const [pendingOverrideReason, setPendingOverrideReason] = useState<
    string | null | undefined
  >(undefined)

  useEffect(() => {
    setPendingOverrideReason(undefined)
  }, [complianceId, highRiskUserOverrideReason])

  const displayedOverrideReason =
    pendingOverrideReason !== undefined
      ? pendingOverrideReason
      : highRiskUserOverrideReason

  const automatedIsHighRisk = highRiskCheck?.isHighRisk === true
  const hasHighRiskUserOverrideReason = Boolean(
    displayedOverrideReason?.trim()
  )
  const effectiveIsHighRisk =
    deriveHighRiskDialogAnswer(
      highRiskCheck?.isHighRisk,
      displayedOverrideReason
    ) === 'yes'
  const userReclassifiedAsNotHighRisk =
    hasHighRiskUserOverrideReason &&
    automatedIsHighRisk &&
    !effectiveIsHighRisk
  const showDisagreeNotHighRiskButton =
    !automatedIsHighRisk && !hasHighRiskUserOverrideReason

  const isAgentHighRisk =
    workflowResultIsHighRisk === true || automatedIsHighRisk

  const view = useMemo(() => {
    return resolveHighRiskView({
      onboardingStep,
      isHighRiskWorkflowRunning,
      isAgentHighRisk,
      isUserElevatedHighRiskLocked,
      hasHighRiskUserOverrideReason,
    })
  }, [
    onboardingStep,
    isHighRiskWorkflowRunning,
    isAgentHighRisk,
    isUserElevatedHighRiskLocked,
    hasHighRiskUserOverrideReason,
  ])

  const openOverrideDialog = useCallback(
    (variant: OverrideDialogVariant, initialReason = '') => {
      setOverrideDialogVariant(variant)
      setDraftReason(initialReason)
      setOverrideDialogOpen(true)
    },
    []
  )

  const handleSubmitOverrideReason = useCallback(() => {
    const reason = draftReason.trim()
    if (!OverrideReasonSchema.safeParse(reason).success) {
      return
    }
    setHighRiskUserOverrideReasonMutation.mutate(
      { reason },
      {
        onSuccess: (updatedCompliance) => {
          setPendingOverrideReason(
            updatedCompliance.highRiskUserOverrideReason?.trim() || null
          )
          setOverrideDialogOpen(false)
          setDraftReason('')
          onHighRiskClassificationUpdated?.(updatedCompliance)
          if (overrideDialogVariant === 'disagreeAutomatedHighRisk') {
            toast.success(t('onboarding.highRiskOverride.successToast'))
            return
          }
          if (overrideDialogVariant === 'disagreeAutomatedNotHighRisk') {
            toast.success(t('onboarding.notHighRiskDisagreeDialog.successToast'))
            return
          }
          toast.success(t('settings.productMetadata.highRiskAnalysis.editSuccess'))
        },
        onError: (err) => {
          if (overrideDialogVariant === 'disagreeAutomatedHighRisk') {
            toast.error(
              err.message || t('onboarding.highRiskOverride.errorToast')
            )
            return
          }
          toast.error(
            err.message ||
              t('settings.productMetadata.highRiskAnalysis.editFailed')
          )
        },
      }
    )
  }, [
    draftReason,
    overrideDialogVariant,
    setHighRiskUserOverrideReasonMutation,
    onHighRiskClassificationUpdated,
    t,
  ])

  const handleAlignWithAutomated = useCallback(() => {
    setHighRiskUserOverrideReasonMutation.mutate(
      { reason: null },
      {
        onSuccess: (updatedCompliance) => {
          setPendingOverrideReason(
            updatedCompliance.highRiskUserOverrideReason?.trim() || null
          )
          setOverrideDialogOpen(false)
          setDraftReason('')
          onHighRiskClassificationUpdated?.(updatedCompliance)
          toast.success(
            t('settings.productMetadata.highRiskAnalysis.clearSuccess')
          )
        },
        onError: (err) => {
          toast.error(
            err.message ||
              t('settings.productMetadata.highRiskAnalysis.editFailed')
          )
        },
      }
    )
  }, [setHighRiskUserOverrideReasonMutation, onHighRiskClassificationUpdated, t])

  if (view == null) {
    return null
  }

  if (view === 'checking') {
    return (
      <div className='flex flex-col items-center justify-center py-16'>
        <Loader2 className='h-12 w-12 animate-spin text-muted-foreground' />
        <p className='mt-6 text-center text-muted-foreground'>
          {t('onboarding.highRiskChecking')}
        </p>
      </div>
    )
  }

  if (view === 'intro') {
    return (
      <div className='space-y-6'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold tracking-tight'>
            {t('onboarding.stepHighRiskCheck')}
          </h2>
          <p className='leading-relaxed text-muted-foreground'>
            {t('onboarding.highRiskFirstStep.intro')}
          </p>
          <p className='leading-relaxed text-muted-foreground'>
            {t('onboarding.highRiskFirstStep.whatWeDo')}
          </p>
          <p className='font-medium text-foreground'>
            {t('onboarding.highRiskFirstStep.whyMatters')}
          </p>
        </div>
        <div className='flex justify-end'>
          <Button onClick={onBeginHighRisk}>
            {t('onboarding.highRiskFirstStep.runCheck')}
          </Button>
        </div>
        {highRiskWorkflowError && (
          <Alert variant='destructive'>
            <AlertDescription>{highRiskWorkflowError}</AlertDescription>
          </Alert>
        )}
      </div>
    )
  }

  if (view === 'user_override_locked') {
    return (
      <>
        <div className='space-y-6'>
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold tracking-tight'>
              {t('onboarding.highRiskResult.title')}
            </h2>
            <HighRiskResultListPanel isHighRisk>
              <ul className={highRiskResultListClassName(true)}>
              <li>
                <span className='font-semibold'>
                  {t(
                    'onboarding.userHighRiskOverrideLocked.automatedCheckLabel'
                  )}
                  :
                </span>{' '}
                {t('onboarding.highRiskMachine')}: {t('common:no')}
              </li>
              {highRiskCheck?.highRiskReasoning?.trim() ? (
                <li>
                  <span className='font-semibold'>
                    {t('onboarding.highRiskReasoningLabel')}:
                  </span>{' '}
                  {highRiskCheck.highRiskReasoning}
                </li>
              ) : null}
              <li>
                <span className='font-semibold'>
                  {t(
                    'onboarding.userHighRiskOverrideLocked.yourClassificationLabel'
                  )}
                  :
                </span>{' '}
                {t('onboarding.highRiskMachine')}: {t('common:yes')}
              </li>
              {highRiskUserOverrideReason ? (
                <li>
                  <span className='font-semibold'>
                    {t('onboarding.highRiskOverride.recordedReasonLabel')}:
                  </span>{' '}
                  {highRiskUserOverrideReason}
                </li>
              ) : null}
            </ul>
            </HighRiskResultListPanel>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.userHighRiskOverrideLocked.body')}
            </p>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.userHighRiskOverrideLocked.nextSteps')}
            </p>
          </div>
          <div className='flex flex-wrap justify-end gap-2'>
            <Button
              type='button'
              variant='warning'
              onClick={() => {
                openOverrideDialog(
                  'userElevatedHighRisk',
                  highRiskUserOverrideReason ?? ''
                )
              }}
              disabled={setHighRiskUserOverrideReasonMutation.isPending}
            >
              {t('onboarding.userHighRiskOverrideDialog.openButton')}
            </Button>
            <Button variant='outline' onClick={onCloseHighRisk}>
              {t('onboarding.close')}
            </Button>
          </div>
        </div>
        <HighRiskOverrideDialog
          open={overrideDialogOpen}
          onOpenChange={setOverrideDialogOpen}
          reason={draftReason}
          onReasonChange={setDraftReason}
          isPending={setHighRiskUserOverrideReasonMutation.isPending}
          onSubmit={handleSubmitOverrideReason}
          variant='userElevatedHighRisk'
          onAlignWithAutomated={handleAlignWithAutomated}
        />
      </>
    )
  }

  if (view === 'agent_locked') {
    return (
      <>
        <div className='space-y-6'>
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold tracking-tight'>
              {t('onboarding.highRiskResult.title')}
            </h2>
            <HighRiskResultListPanel isHighRisk>
              <ul className={highRiskResultListClassName(true)}>
              <li>
                <span className='font-semibold'>
                  {t('onboarding.highRiskMachine')}:
                </span>{' '}
                {t('common:yes')}
              </li>
              <li>
                <span className='font-semibold'>
                  {t('onboarding.highRiskCategoryAnnexIv')}:
                </span>{' '}
                {highRiskCheck?.highRiskCategory ? (
                  <span>
                    {highRiskCheck.highRiskCategory} -{' '}
                    {t(
                      `onboarding.annexIvCategory.${highRiskCheck.highRiskCategory}`
                    )}
                  </span>
                ) : (
                  <span>{t('onboarding.highRiskCategoryNotSpecified')}</span>
                )}
              </li>
              {highRiskCheck?.highRiskReasoning && (
                <li>
                  <span className='font-semibold'>
                    {t('onboarding.highRiskReasoningLabel')}:
                  </span>{' '}
                  {highRiskCheck.highRiskReasoning}
                </li>
              )}
            </ul>
            </HighRiskResultListPanel>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.highRiskLocked.body')}
            </p>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.highRiskLocked.nextSteps')}
            </p>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.highRiskOverride.lockedHint')}
            </p>
          </div>
          <div className='flex flex-wrap justify-end gap-2'>
            <Button
              variant='warning'
              onClick={() => {
                openOverrideDialog('disagreeAutomatedHighRisk')
              }}
            >
              {t('onboarding.highRiskOverride.disagreeButton')}
            </Button>
            <Button variant='outline' onClick={onCloseHighRisk}>
              {t('onboarding.close')}
            </Button>
          </div>
        </div>
        <HighRiskOverrideDialog
          open={overrideDialogOpen}
          onOpenChange={setOverrideDialogOpen}
          reason={draftReason}
          onReasonChange={setDraftReason}
          isPending={setHighRiskUserOverrideReasonMutation.isPending}
          onSubmit={handleSubmitOverrideReason}
          variant='disagreeAutomatedHighRisk'
        />
      </>
    )
  }

  const resultListIsHighRisk = userReclassifiedAsNotHighRisk
    ? false
    : effectiveIsHighRisk

  return (
    <>
      <div className='space-y-6'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold tracking-tight'>
            {t('onboarding.highRiskResult.title')}
          </h2>
          <HighRiskResultListPanel isHighRisk={resultListIsHighRisk}>
            <ul className={highRiskResultListClassName(resultListIsHighRisk)}>
            {userReclassifiedAsNotHighRisk ? (
              <>
                <li>
                  <span className='font-semibold'>
                    {t(
                      'onboarding.userHighRiskOverrideLocked.automatedCheckLabel'
                    )}
                    :
                  </span>{' '}
                  {t('onboarding.highRiskMachine')}: {t('common:yes')}
                </li>
                {highRiskCheck?.highRiskCategory ? (
                  <li>
                    <span className='font-semibold'>
                      {t('onboarding.highRiskCategoryAnnexIv')}:
                    </span>{' '}
                    <span>
                      {highRiskCheck.highRiskCategory} -{' '}
                      {t(
                        `onboarding.annexIvCategory.${highRiskCheck.highRiskCategory}`
                      )}
                    </span>
                  </li>
                ) : null}
                {highRiskCheck?.highRiskReasoning ? (
                  <li>
                    <span className='font-semibold'>
                      {t('onboarding.highRiskReasoningLabel')}:
                    </span>{' '}
                    {highRiskCheck.highRiskReasoning}
                  </li>
                ) : null}
                <li>
                  <span className='font-semibold'>
                    {t(
                      'onboarding.userHighRiskOverrideLocked.yourClassificationLabel'
                    )}
                    :
                  </span>{' '}
                  {t('onboarding.highRiskMachine')}: {t('common:no')}
                </li>
                {displayedOverrideReason ? (
                  <li>
                    <span className='font-semibold'>
                      {t('onboarding.highRiskOverride.recordedReasonLabel')}:
                    </span>{' '}
                    {displayedOverrideReason}
                  </li>
                ) : null}
              </>
            ) : (
              <>
                <li>
                  <span className='font-semibold'>
                    {t('onboarding.highRiskMachine')}:
                  </span>{' '}
                  {effectiveIsHighRisk ? t('common:yes') : t('common:no')}
                </li>
                {hasHighRiskUserOverrideReason && displayedOverrideReason ? (
                  <li>
                    <span className='font-semibold'>
                      {t('onboarding.highRiskOverride.recordedReasonLabel')}:
                    </span>{' '}
                    {displayedOverrideReason}
                  </li>
                ) : null}
              </>
            )}
          </ul>
          </HighRiskResultListPanel>
          {!userReclassifiedAsNotHighRisk && highRiskCheck?.highRiskReasoning ? (
            <p className='text-sm leading-relaxed text-muted-foreground'>
              {highRiskCheck.highRiskReasoning}
            </p>
          ) : null}
          {showDisagreeNotHighRiskButton ? (
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.notHighRiskDisagree.hint')}
            </p>
          ) : null}
          <p className='leading-relaxed text-muted-foreground'>
            {userReclassifiedAsNotHighRisk
              ? t('onboarding.highRiskOverride.continueAfterOverride')
              : t('onboarding.highRiskResultOk.nextStep')}
          </p>
        </div>
        <div className='flex flex-wrap justify-end gap-2'>
          {showDisagreeNotHighRiskButton ? (
            <Button
              type='button'
              variant='warning'
              onClick={() => {
                openOverrideDialog('disagreeAutomatedNotHighRisk')
              }}
              disabled={setHighRiskUserOverrideReasonMutation.isPending}
            >
              {t('onboarding.notHighRiskDisagreeDialog.openButton')}
            </Button>
          ) : null}
          <Button onClick={onContinue}>{t('onboarding.intro.continue')}</Button>
        </div>
      </div>
      <HighRiskOverrideDialog
        open={overrideDialogOpen}
        onOpenChange={setOverrideDialogOpen}
        reason={draftReason}
        onReasonChange={setDraftReason}
        isPending={setHighRiskUserOverrideReasonMutation.isPending}
        onSubmit={handleSubmitOverrideReason}
        variant='disagreeAutomatedNotHighRisk'
      />
    </>
  )
}
